import { useQuery, type UseQueryOptions, type UseQueryResult } from '@tanstack/react-query'

import { getAllEvents } from '@/utils/api/membership-service'
import { getStoredEventColor } from '@/features/calendar/utils/color-store'
import type { CPDEventResponseDTO } from '@/utils/api/membership-service/models/cpdevent-response-dt-o'

import type { ICalendarEvent } from '../interfaces'

const DEFAULT_PAGE_SIZE = 200
const FALLBACK_LOCATION = 'Location to be confirmed'

type QueryKey = ['calendar', 'events']

function resolveCategory(isOnline: boolean): ICalendarEvent['category'] {
  return isOnline ? 'webinar' : 'training'
}

function resolveColor(
  category: ICalendarEvent['category'],
  statusCandidate: unknown,
): ICalendarEvent['color'] {
  if (typeof statusCandidate === 'string') {
    const status = statusCandidate.trim().toLowerCase()
    if (status === 'cancelled') return 'red'
    if (status === 'completed') return 'green'
    if (status === 'ongoing') return 'orange'
    if (status === 'draft') return 'purple'
    if (status.includes('registration') || status === 'registration closed') return 'yellow'
    if (status === 'full') return 'red'
  }

  switch (category) {
    case 'webinar':
      return 'purple'
    case 'networking':
      return 'yellow'
    default:
      return 'blue'
  }
}

function toValidDateTimeString(value?: string | null): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  if (!trimmed) return undefined
  const parsed = new Date(trimmed)
  if (Number.isNaN(parsed.getTime())) return undefined
  return trimmed
}

function sanitiseString(value?: string | null): string | undefined {
  if (typeof value !== 'string') return undefined
  const trimmed = value.trim()
  return trimmed.length > 0 ? trimmed : undefined
}

function toNonNegative(value?: number | null): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return undefined
  }
  return value
}

function mapDtoToCalendarEvent(dto: CPDEventResponseDTO, index: number): ICalendarEvent | null {
  const startAt = toValidDateTimeString(dto.startAt)
  if (!startAt) {
    return null
  }

  const endAt = toValidDateTimeString(dto.endAt) ?? startAt
  const sourceId = typeof dto.id === 'number' && Number.isFinite(dto.id) ? dto.id : undefined
  const id = sourceId != null ? String(sourceId) : `calendar-event-${index}`
  const isOnline = dto.isOnline ?? false
  const category = resolveCategory(isOnline)
  const status = sanitiseString(dto.status ? String(dto.status) : undefined)
  const normalizedStatus = status?.toLowerCase()
  const storedColor = getStoredEventColor(sourceId)
  const color = storedColor ?? resolveColor(category, status)

  const maxParticipants = toNonNegative(dto.maxParticipants)
  const currentParticipants = toNonNegative(dto.currentParticipants)
  const availableSpots =
    typeof maxParticipants === 'number' && typeof currentParticipants === 'number'
      ? Math.max(maxParticipants - currentParticipants, 0)
      : undefined

  return {
    id,
    sourceId,
    title: sanitiseString(dto.title) ?? 'CPD Event',
    description: sanitiseString(dto.description),
    category,
    location: isOnline ? 'Online' : sanitiseString(dto.venue) ?? FALLBACK_LOCATION,
    startAt,
    endAt,
    color,
    availableSpots,
    isFeatured: normalizedStatus === 'ongoing',
    points: typeof dto.points === 'number' && Number.isFinite(dto.points) ? dto.points : 0,
    isOnline,
    onlineLink: sanitiseString(dto.onlineLink),
    facilitators: sanitiseString(dto.facilitators),
    registrationDeadline: toValidDateTimeString(dto.registrationDeadline),
    cost: toNonNegative(dto.cost),
    status,
    registrations: typeof currentParticipants === 'number' ? currentParticipants : 0,
    theme: sanitiseString(dto.theme),
  }
}

async function fetchCalendarEvents(): Promise<ICalendarEvent[]> {
  const response = await getAllEvents({ page: 0, size: DEFAULT_PAGE_SIZE })
  const payload = response.data
  const entries = Array.isArray(payload?.content) ? payload.content : []

  const mapped = entries
    .map((event, index) => mapDtoToCalendarEvent(event, index))
    .filter((event): event is ICalendarEvent => Boolean(event))

  return mapped.sort((a, b) => {
    const startA = new Date(a.startAt).getTime()
    const startB = new Date(b.startAt).getTime()
    if (Number.isNaN(startA) || Number.isNaN(startB)) return 0
    return startA - startB
  })
}

type CalendarEventsQueryOptions = Omit<
  UseQueryOptions<ICalendarEvent[], Error, ICalendarEvent[], QueryKey>,
  'queryKey' | 'queryFn'
>

export function useCalendarEventsQuery(
  options?: CalendarEventsQueryOptions,
): UseQueryResult<ICalendarEvent[], Error> {
  return useQuery({
    queryKey: ['calendar', 'events'],
    queryFn: fetchCalendarEvents,
    staleTime: 5 * 60 * 1000,
    ...options,
  })
}
